import ContentTile from 'teacher-ui-client/components/tiles/content'
import { formatNumber, t } from 'ember-intl'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import UiButton from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { on } from '@ember/modifier'
import { hash } from '@ember/helper'
import { TableContainer, TH, TD, TRHead, Table } from 'teacher-ui-client/components/reporting/table'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import joinList from 'teacher-ui-client/helpers/join-list'
import { eq } from 'ember-truth-helpers'
import type { TOC } from '@ember/component/template-only'
import { htmlSafe } from '@ember/template'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import { LinkTo } from '@ember/routing'
import type { Task } from 'ember-concurrency'
import { Product } from 'teacher-ui-client/utils/products'
import { posthogEvent } from 'teacher-ui-client/modifiers/posthog'
import Component from '@glimmer/component'
import {
  type BaseReport,
  type BaseReportMetaPaging,
  type BaseReportMetaStandardsSet,
} from 'teacher-ui-client/services/reporting'

function avgScoreColor(score: Nullable<number>): string {
  if (typeof score !== 'number') return ''
  switch (true) {
    case score <= 49:
      return 'text-watermelony-red-350'
    case score <= 79:
      return 'text-juicy-orange-350'
    case score <= 100:
      return 'text-forest-green-350'
    default:
      return ''
  }
}

export type DistributionBands = [
  { band: '0-49'; total_students: number; percentage: number },
  { band: '50-79'; total_students: number; percentage: number },
  { band: '80-100'; total_students: number; percentage: number },
]

export interface StandardsResultsReportData extends BaseReport {
  aggregate: {
    total_students: number
  }
  results: {
    average_score: Nullable<number>
    content_level: string
    lesson: number
    lesson_concepts: string[]
    standards: string[]
    student_count_distribution: DistributionBands
    total_students: number
  }[]
  meta: BaseReportMetaPaging & BaseReportMetaStandardsSet
}

interface Args {
  reportData: StandardsResultsReportData
  exportCsv: Task<void, never[]>
  isReloading?: boolean
  studentReportRoute: string
  product?: Product
}

interface Signature {
  Args: Args
}

export class StandardsResults extends Component<Signature> {
  get isFastPhonics() {
    return this.args.product === Product.FP
  }

  <template>
    <ContentTile class="space-y-4 p-6">
      <StandardsResultsHead
        @reportData={{@reportData}}
        @exportCsv={{@exportCsv}}
        @isFastPhonics={{this.isFastPhonics}}
        @product={{@product}}
      />

      <TableContainer>
        <Table data-test-results-table @minWidth="min-w-5xl">
          <thead>
            <TRHead>
              <TH @sortKey="content_level" class="w-28">
                {{t "reporting.standardsPerformance.standardsResultsTile.table.contentLevel" htmlSafe=true}}
              </TH>
              <TH @sortKey="lesson">{{if
                  this.isFastPhonics
                  (t "reporting.standardsPerformance.standardsResultsTile.table.lessonFocusFp" htmlSafe=true)
                  (t "reporting.standardsPerformance.standardsResultsTile.table.lessonFocus" htmlSafe=true)
                }}</TH>
              <TH>{{t "reporting.standardsPerformance.standardsResultsTile.table.standards"}}</TH>
              <TH @sortKey="total_students" class="w-32">
                {{t "reporting.standardsPerformance.standardsResultsTile.table.numberStudents" htmlSafe=true}}
              </TH>
              <TH>
                {{t "reporting.standardsPerformance.standardsResultsTile.table.studentDistribution" htmlSafe=true}}
                <span class="print:hidden">
                  <PhosphorIcon @icon="phosphor-info-fill" />
                  <Tooltip
                    @text={{t "reporting.standardsPerformance.standardsResultsTile.table.distributionTooltip"}}
                  />
                </span>
              </TH>
              <TH @sortKey="average_score" class="w-28">
                {{t "reporting.standardsPerformance.standardsResultsTile.table.averageScore" htmlSafe=true}}
              </TH>
              <TH class="w-36 print:hidden" />
            </TRHead>
          </thead>
          <tbody data-test-results-table-body class="divide-y-2 divide-neutral-50 {{if @isReloading 'opacity-50'}}">
            {{#each @reportData.results as |resultItem|}}
              <tr>
                <TD>{{resultItem.content_level}}</TD>
                <TD>
                  {{#each resultItem.lesson_concepts as |concept index|}}
                    <div class={{if (eq index 0) "font-semibold"}}>{{concept}}</div>
                  {{/each}}
                </TD>
                <TD>{{joinList resultItem.standards ", "}}</TD>
                <TD class="font-semibold">
                  <span class="text-xl"><PhosphorIcon @icon="phosphor-users-three-bold" class="mr-2" /></span>
                  {{formatValue resultItem.total_students}}
                </TD>
                <TD>
                  <StudentCountDistribution @distribution={{resultItem.student_count_distribution}} />
                </TD>
                <TD class="font-semibold" @textColor={{avgScoreColor resultItem.average_score}}>
                  {{formatValue resultItem.average_score append="%"}}
                </TD>
                <TD class="print:hidden">
                  <LinkTo
                    data-test-lesson-performance-link={{resultItem.lesson}}
                    @route={{@studentReportRoute}}
                    @query={{hash selectedLesson=resultItem.lesson}}
                  >
                    <div class="text-oceany-blue-350 items-center text-center">
                      <PhosphorIcon @icon="phosphor-arrow-circle-right-fill" class="h-5 w-5" />
                      <div class="font-bold text-nowrap underline">
                        {{t "reporting.standardsPerformance.standardsResultsTile.table.studentReport"}}
                      </div>
                    </div>
                  </LinkTo>
                </TD>
              </tr>
            {{/each}}
          </tbody>
        </Table>
      </TableContainer>
      <BasePageSelector
        class="my-4"
        @pageCount={{@reportData.meta.total_pages}}
        @itemCount={{@reportData.meta.total_entries}}
      />

    </ContentTile>
  </template>
}

function basisPercentage(perc: number) {
  return htmlSafe(`flex-basis: ${perc}%`)
}

interface StudentCountDistributionSignature {
  Element: HTMLDivElement
  Args: { distribution?: DistributionBands }
}

export const StudentCountDistribution: TOC<StudentCountDistributionSignature> = <template>
  <div class="group flex gap-0.5 overflow-hidden rounded-full border-2 border-white bg-white" ...attributes>
    {{#each @distribution as |distributionItem|}}
      <div
        {{! when a group item (and thus the group) are hovered: fade the unhovered siblings in the group }}
        class="px-1 py-0.5 text-center text-xs font-medium group-hover:opacity-50 hover:opacity-100!
          {{if (eq distributionItem.band '0-49') 'bg-watermelony-red-100'}}
          {{if (eq distributionItem.band '50-79') 'bg-yolky-yellow-100'}}
          {{if (eq distributionItem.band '80-100') 'bg-forest-green-150'}}"
        style={{basisPercentage distributionItem.percentage}}
      >
        {{formatNumber distributionItem.total_students}}
        <Tooltip @text="{{formatNumber distributionItem.percentage}}%" />
      </div>
    {{/each}}
  </div>
</template>

interface HeadSignature {
  Args: {
    reportData: StandardsResultsReportData
    exportCsv: Task<unknown, unknown[]>
    isFastPhonics?: boolean
    product?: string
  }
}

export const StandardsResultsHead: TOC<HeadSignature> = <template>
  <div class="space-y-2">
    <h4>{{t "reporting.standardsPerformance.standardsResults"}}</h4>
    <div>{{t
        "reporting.standardsPerformance.standardsResultsTile.showingDataForStudents"
        count=@reportData.aggregate.total_students
      }}</div>
    <div class="flex items-end gap-2">
      <div class="flex gap-6">
        <div class="space-x-2">
          {{! template-lint-disable no-bare-strings }}
          <PhosphorIcon @icon="phosphor-circle-fill" class="text-watermelony-red-200" /><span
            class="text-neutral-300"
          >0-49%</span>
        </div>
        <div class="space-x-2">
          {{! template-lint-disable no-bare-strings }}
          <PhosphorIcon @icon="phosphor-circle-fill" class="text-yolky-yellow-300" /><span
            class="text-neutral-300"
          >50-79%</span>
        </div>
        <div class="space-x-2">
          {{! template-lint-disable no-bare-strings }}
          <PhosphorIcon @icon="phosphor-circle-fill" class="text-forest-green-300" /><span
            class="text-neutral-300"
          >80-100%</span>
        </div>
      </div>
      <span class="print:hidden">
        <PhosphorIcon @icon="phosphor-info-fill" />
        <Tooltip
          @text={{if
            @isFastPhonics
            (t "reporting.standardsPerformance.standardsResultsTile.legendTooltipFp")
            (t "reporting.standardsPerformance.standardsResultsTile.legendTooltip")
          }}
        />
      </span>
      <div class="ml-auto">
        <UiButton
          class="inline-flex items-center gap-1 print:hidden"
          @appearance={{ButtonAppearance.regular}}
          @disabled={{@exportCsv.isRunning}}
          {{on "click" @exportCsv.perform}}
          {{posthogEvent "export-report-csv" report="standards-performance" product=@product}}
        >
          <PhosphorIcon @icon="phosphor-file-x-fill" />
          {{t "reporting.standardsPerformance.standardsResultsTile.exportCsv"}}
        </UiButton>
      </div>
    </div>

  </div>
</template>
